import { spawn } from "node:child_process";
import { writeFile } from "node:fs/promises";
import { logger } from "./log.js";

const log = logger.child("diff");

/**
 * Files that change frequently but never carry reviewable UI behavior. Matched
 * against the changed path (patterns without "/" match the basename anywhere).
 * User-configured `diff.exclude` patterns are applied on top of these.
 */
export const defaultExcludePatterns = [
  // Lockfiles
  "pnpm-lock.yaml",
  "package-lock.json",
  "yarn.lock",
  "bun.lock",
  "bun.lockb",
  "deno.lock",
  "Cargo.lock",
  "composer.lock",
  "Gemfile.lock",
  "poetry.lock",
  "uv.lock",
  "go.sum",
  "flake.lock",
  // Minified and generated output
  "*.min.js",
  "*.min.css",
  "*.map",
];

export const defaultDiffLimits = {
  /** Per-file changed-line budget before a file is summarized instead of diffed. */
  maxFileChangedLines: 2_000,
  /** Total diff budget in bytes before the body is truncated. */
  maxTotalBytes: 512 * 1024,
} as const;

export interface ChangedFile {
  path: string;
  /** Added line count, or null for binary files. */
  added: number | null;
  /** Deleted line count, or null for binary files. */
  deleted: number | null;
  binary: boolean;
}

export interface SkippedFile {
  file: ChangedFile;
  reason: string;
}

export interface WriteDiffOptions {
  exclude?: string[];
  maxFileChangedLines?: number;
  maxTotalBytes?: number;
  /** Overrides REVUIW_BASE_SHA. */
  base?: string;
  /** Overrides REVUIW_HEAD_SHA. */
  head?: string;
}

/**
 * Write the pull request diff for the review agents, omitting content that
 * would waste their context: binary files (images, fonts, archives), lockfiles
 * and other generated artifacts, and files whose diff exceeds the per-file
 * line budget. Omitted files are still listed at the end of the diff so agents
 * know they changed.
 */
export async function writePullRequestDiff(cwd: string, diffPath: string, options: WriteDiffOptions = {}): Promise<void> {
  const base = options.base ?? process.env.REVUIW_BASE_SHA;
  const head = options.head ?? process.env.REVUIW_HEAD_SHA ?? "HEAD";
  const range = base ? [base, head] : ["HEAD^", "HEAD"];
  const maxTotalBytes = options.maxTotalBytes ?? defaultDiffLimits.maxTotalBytes;

  try {
    const numstat = await git(cwd, ["diff", "--no-ext-diff", "--numstat", ...range]);
    const files = parseNumstat(numstat);
    const { included, skipped } = partitionChangedFiles(files, {
      exclude: options.exclude ?? [],
      maxFileChangedLines: options.maxFileChangedLines ?? defaultDiffLimits.maxFileChangedLines,
    });

    let body = "";
    if (included.length > 0) {
      const pathspec = skipped.length > 0
        ? ["--", ".", ...skipped.map((entry) => `:(exclude,literal)${entry.file.path}`)]
        : [];
      body = await git(cwd, ["diff", "--no-ext-diff", ...range, ...pathspec]);
      body = truncateDiff(body, maxTotalBytes);
    }

    const sections = [];
    if (body.trim()) sections.push(body.trimEnd());
    if (skipped.length > 0) sections.push(renderSkippedSection(skipped));
    if (sections.length === 0) sections.push("No file changes detected in the diff range.");
    await writeFile(diffPath, `${sections.join("\n\n")}\n`);
    log.info(`Diff ready: ${included.length} file(s) included, ${skipped.length} omitted`);
    for (const entry of skipped) log.debug(`omitted ${entry.file.path}: ${entry.reason}`);
  } catch (error) {
    log.warn(`Could not produce a diff (${error instanceof Error ? error.message : String(error)}); agents will inspect the working tree`);
    await writeFile(diffPath, "Diff unavailable. Inspect the current working tree.\n");
  }
}

/** Parse `git diff --numstat` output, resolving rename notation to the new path. */
export function parseNumstat(output: string): ChangedFile[] {
  const files: ChangedFile[] = [];
  for (const line of output.split("\n")) {
    if (!line.trim()) continue;
    const [added, deleted, ...rest] = line.split("\t");
    if (rest.length === 0 || added === undefined || deleted === undefined) continue;
    files.push({
      path: resolveRenamePath(rest.join("\t")),
      added: added === "-" ? null : Number(added),
      deleted: deleted === "-" ? null : Number(deleted),
      binary: added === "-" && deleted === "-",
    });
  }
  return files;
}

export function partitionChangedFiles(
  files: ChangedFile[],
  options: { exclude: string[]; maxFileChangedLines: number },
): { included: ChangedFile[]; skipped: SkippedFile[] } {
  const patterns = [...defaultExcludePatterns, ...options.exclude];
  const included: ChangedFile[] = [];
  const skipped: SkippedFile[] = [];

  for (const file of files) {
    if (file.binary) {
      skipped.push({ file, reason: "binary file" });
      continue;
    }
    const pattern = patterns.find((candidate) => matchesPattern(file.path, candidate));
    if (pattern) {
      skipped.push({ file, reason: `matches exclude pattern "${pattern}"` });
      continue;
    }
    const changedLines = (file.added ?? 0) + (file.deleted ?? 0);
    if (changedLines > options.maxFileChangedLines) {
      skipped.push({ file, reason: `too large (${changedLines} changed lines, limit ${options.maxFileChangedLines})` });
      continue;
    }
    included.push(file);
  }

  return { included, skipped };
}

/**
 * Match a changed path against a glob pattern. Patterns without "/" match the
 * basename anywhere in the tree (like .gitignore); "**" spans directories.
 */
export function matchesPattern(filePath: string, pattern: string): boolean {
  const target = pattern.includes("/") ? filePath : filePath.split("/").at(-1)!;
  return globToRegExp(pattern).test(target);
}

function globToRegExp(pattern: string): RegExp {
  const source = pattern
    .replace(/[.+^${}()|[\]\\]/g, "\\$&")
    .replaceAll("**/", "\u0001")
    .replaceAll("**", "\u0002")
    .replaceAll("*", "[^/]*")
    .replaceAll("?", "[^/]")
    .replaceAll("\u0001", "(?:.*/)?")
    .replaceAll("\u0002", ".*");
  return new RegExp(`^(?:${source})$`);
}

/** `src/{old.ts => new.ts}` or `old.ts => new.ts` → the post-rename path. */
function resolveRenamePath(value: string): string {
  const expanded = value
    .replace(/\{([^{}]*) => ([^{}]*)\}/g, (_match, _from: string, to: string) => to)
    .replace(/\/{2,}/g, "/");
  const arrow = expanded.indexOf(" => ");
  return arrow >= 0 ? expanded.slice(arrow + 4) : expanded;
}

function renderSkippedSection(skipped: SkippedFile[]): string {
  const lines = skipped.map(({ file, reason }) => {
    const stats = file.binary ? "" : ` (+${file.added ?? 0}/-${file.deleted ?? 0})`;
    return `- ${file.path}${stats} — ${reason}`;
  });
  return `=== Changed files omitted from this diff ===\nThese files changed in the pull request but their content was omitted. Read them from the working tree if they matter to the review.\n${lines.join("\n")}`;
}

function truncateDiff(body: string, maxTotalBytes: number): string {
  if (Buffer.byteLength(body, "utf8") <= maxTotalBytes) return body;
  let text = Buffer.from(body, "utf8").subarray(0, maxTotalBytes).toString("utf8");
  text = text.replace(/�+$/, ""); // drop a multibyte character split at the cut
  const lastNewline = text.lastIndexOf("\n");
  if (lastNewline > 0) text = text.slice(0, lastNewline + 1);
  return `${text}\n[revuiw] Diff truncated: output exceeded ${maxTotalBytes} bytes. Read remaining files from the working tree.`;
}

function git(cwd: string, args: string[]): Promise<string> {
  return new Promise((resolve, reject) => {
    const child = spawn("git", args, { cwd, stdio: ["ignore", "pipe", "pipe"] });
    let stdout = "";
    let stderr = "";
    child.stdout.setEncoding("utf8");
    child.stderr.setEncoding("utf8");
    child.stdout.on("data", (chunk: string) => { stdout += chunk; });
    child.stderr.on("data", (chunk: string) => { stderr += chunk; });
    child.once("error", reject);
    child.once("close", (code) => {
      if (code === 0) resolve(stdout);
      else reject(new Error(`git ${args.join(" ")} exited with code ${code}: ${stderr.trim()}`));
    });
  });
}
